/* Mesure d'audience — pousse dans `dataLayer` les événements que le conteneur
   GTM de la cliente attend, au format du site d'origine.

   Le conteneur (GTM-MZJQNG7X) n'est pas à nous et n'est pas modifié : ses
   déclencheurs sont des événements personnalisés `<nom>_aw` et ses variables
   lisent `page_type`, `page_location`, `env_country`, `env_language`,
   `ecommerce.items`, `ecommerce.value`, `ecommerce.currency`,
   `ecommerce.item_list_name`, `search_term`, `results_count`. Le relevé
   complet du contrat, événement par événement, est dans
   .claude/docs/datalayer-original.md. Ce script le reproduit ; il ne décide
   rien du consentement (c'est le conteneur qui filtre ses balises avec les
   événements `didomi-*` du SDK) et ne charge rien.

   Répartition des rôles :
   - `snippets/analytics.liquid` rend la configuration de page (#analytics-page :
     suffixe, page_type, pays, langue, devise, préfixe de locale) et charge ce
     fichier en `defer` ;
   - `snippets/analytics-item.liquid` construit l'objet « item » d'un produit,
     que les sections et snippets déposent dans le markup ;
   - ce script lit tout cela et pousse.

   Contrat de markup (ce que le script cherche dans le DOM) :

   - #analytics-page            JSON de configuration (voir le snippet).
   - script[type="application/json"][data-analytics="<événement>"]
                                charge utile d'un événement de page, poussée
                                une fois après page_view, dans l'ordre du
                                document (view_item_list, view_item, search).
   - [data-analytics-list-name] (et [data-analytics-list-id] facultatif)
                                une grille de produits : le clic sur un lien
                                d'une <product-card>[data-analytics-item]
                                qu'elle contient pousse select_item.
   - <product-card data-analytics-item="<item JSON>">
   - cart-drawer > dialog       l'ouverture du panneau pousse view_cart s'il
                                contient des lignes ;
     [data-cart-content][data-analytics-total]  total du panier en centimes ;
     [data-cart-line][data-analytics-item][data-quantity]  une ligne, dont
                                les boutons [data-cart-step="±1"] poussent
                                add_to_cart / remove_from_cart d'une unité et
                                [data-cart-remove] remove_from_cart de la ligne.
   - l'événement `cart:added` de assets/cart.js (detail.item = réponse de
     /cart/add.js) pousse add_to_cart.

   Écarts voulus avec l'original : depuis la recherche, `item_list_name` vaut
   « Select product from search <terme> » au lieu de « … search undefined »
   (bug du site) ; pas de add_to_wishlist (pas de wishlist dans le thème). */

(() => {
  const configNode = document.getElementById('analytics-page');
  if (!configNode) return;

  let config;
  try {
    config = JSON.parse(configNode.textContent);
  } catch (error) {
    return;
  }

  const suffix = config.suffix ? `_${config.suffix}` : '';

  /* Lecture tolérante d'un JSON posé dans le markup : un attribut vide ou
     mal formé ne doit jamais casser la page. */
  const parse = (text) => {
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  };

  const push = (name, data) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: name + suffix }, data));
  };

  /* L'original pousse tout sauf page_view dans un setTimeout(…, 1) : même
     ordre d'arrivée pour le conteneur. */
  const pushLater = (name, data) => setTimeout(() => push(name, data), 1);

  const round = (value) => Math.round(value * 100) / 100;

  /* ---- page_view : d'abord, puis les charges utiles de la page ---- */

  push('page_view', {
    page_path: location.pathname,
    page_type: config.page_type,
    page_location: location.href,
    env_country: config.env_country,
    env_language: config.env_language,
  });

  document.querySelectorAll('script[type="application/json"][data-analytics]').forEach((node) => {
    if (node.dataset.analyticsSent) return;
    node.dataset.analyticsSent = 'true';
    const data = parse(node.textContent);
    if (data) pushLater(node.dataset.analytics, data);
  });

  /* ---- select_item : clic sur une carte d'une grille ---- */

  document.addEventListener('click', (event) => {
    const link = event.target.closest('product-card a[href]');
    if (!link) return;
    const card = link.closest('product-card[data-analytics-item]');
    const list = link.closest('[data-analytics-list-name]');
    if (!card || !list) return;
    const item = parse(card.dataset.analyticsItem);
    if (!item) return;
    const ecommerce = { items: [item], currency: item.currency };
    if (list.dataset.analyticsListId) ecommerce.item_list_id = list.dataset.analyticsListId;
    ecommerce.item_list_name = list.dataset.analyticsListName;
    /* Synchrone : la page va changer, un setTimeout pourrait ne jamais tirer. */
    push('select_item', { ecommerce });
  });

  /* ---- add_to_cart depuis la fiche : réponse de /cart/add.js ---- */

  /* Un montant de l'API Ajax est en centimes (69000) ; le script Global-e,
     qui intercepte les appels au panier sur les marchés qu'il sert, le
     renvoie formaté en chaîne (« 690.00 »). Les deux donnent 690. */
  const money = (value) => {
    if (typeof value === 'string') return round(parseFloat(value.replace(',', '.')) || 0);
    return round((Number(value) || 0) / 100);
  };

  const rootUrl = config.root_url && config.root_url !== '/' ? config.root_url : '';
  const stripTags = (html) => {
    const box = document.createElement('div');
    box.innerHTML = html || '';
    return (box.textContent || '').trim();
  };

  /* L'item du produit de la page, s'il y en a un (charge utile view_item de la
     fiche) : la base la plus sûre, la réponse Ajax pouvant manquer de handle
     ou de description selon qui la renvoie. */
  const pageItem = () => {
    const node = document.querySelector('script[type="application/json"][data-analytics="view_item"]');
    const data = node && parse(node.textContent);
    return data && data.ecommerce && data.ecommerce.items && data.ecommerce.items[0];
  };

  document.addEventListener('cart:added', (event) => {
    const added = event.detail && event.detail.item;
    if (!added || !added.product_id) return;
    const price = money(added.price);
    const base = pageItem();
    const item = Object.assign(
      base && base.item_id === String(added.product_id)
        ? base
        : {
            item_id: String(added.product_id),
            item_product_id: String(added.product_id),
            item_product_title: added.product_title,
            item_name: added.product_title,
            description: stripTags(added.product_description),
            availableForSale: true,
            unique: false,
            currency: config.currency,
            url: added.handle ? `${rootUrl}/products/${added.handle}` : added.url || '',
          },
      {
        price,
        item_variant_id: String(added.variant_id),
        item_variant_title: added.variant_title,
        item_variant_name: added.variant_title,
        sku: added.sku,
        quantity: added.quantity || 1,
      }
    );
    pushLater('add_to_cart', { ecommerce: { currency: item.currency, value: price, items: [item] } });
  });

  /* ---- panier : view_cart à l'ouverture, ±1 et suppression sur les lignes ---- */

  const lineItem = (line) => {
    const item = parse(line.dataset.analyticsItem);
    if (!item) return null;
    item.quantity = Number(line.dataset.quantity) || item.quantity || 1;
    return item;
  };

  const cartEcommerce = (drawer) => {
    const lines = [...drawer.querySelectorAll('[data-cart-line][data-analytics-item]')];
    const items = lines.map(lineItem).filter(Boolean);
    if (!items.length) return null;
    const content = drawer.querySelector('[data-cart-content]');
    const total = content && content.dataset.analyticsTotal;
    const value = total ? round(Number(total) / 100) : round(items.reduce((sum, item) => sum + item.price * item.quantity, 0));
    return { currency: items[0].currency, value, items };
  };

  const watchDrawer = (drawer) => {
    const dialog = drawer.querySelector('dialog');
    if (!dialog) return;
    new MutationObserver(() => {
      if (!dialog.open) return;
      const ecommerce = cartEcommerce(drawer);
      if (ecommerce) pushLater('view_cart', { ecommerce });
    }).observe(dialog, { attributes: true, attributeFilter: ['open'] });

    drawer.addEventListener('click', (event) => {
      const line = event.target.closest('[data-cart-line][data-analytics-item]');
      if (!line) return;
      const item = lineItem(line);
      if (!item) return;
      const step = event.target.closest('[data-cart-step]');
      const remove = event.target.closest('[data-cart-remove]');
      if (!step && !remove) return;

      if (step && Number(step.dataset.cartStep) > 0) {
        const one = Object.assign({}, item, { quantity: 1 });
        pushLater('add_to_cart', { ecommerce: { currency: one.currency, value: one.price, items: [one] } });
        return;
      }
      /* « − » retire une unité, « Supprimer » toute la ligne : value = prix
         unitaire pour une unité, prix × quantité au-delà, comme l'original. */
      const quantity = remove ? item.quantity : 1;
      const removed = Object.assign({}, item, { quantity });
      const value = quantity === 1 ? removed.price : round(removed.price * quantity);
      pushLater('remove_from_cart', { ecommerce: { currency: removed.currency, value, items: [removed] } });
    });
  };

  const drawer = document.querySelector('cart-drawer');
  if (drawer) watchDrawer(drawer);
})();
